import { useEffect, useState } from "react";
import { ClipboardCheck, Upload, FileText, Loader2, ShieldAlert, ScrollText, Trash2, Info, Clock, Download } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/pages/Kennisbank";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ACCENT = { chip: "bg-indigo-600", bar: "from-indigo-400 to-indigo-700", text: "text-indigo-600" };

const TYPE_STYLE = {
  advies: { cls: "bg-blue-100 text-blue-800 border-blue-200", dot: "bg-blue-500" },
  instemming: { cls: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  overig: { cls: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const SignaalKaart = ({ s }) => {
  const st = TYPE_STYLE[s.type] || TYPE_STYLE.overig;
  return (
    <div data-testid={`or-signaal-${s.artikel.replace(/\s/g, "-")}`} className="border border-slate-200 rounded-xl bg-white p-4">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} /> {s.type_label}
        </span>
        <span className="text-xs text-slate-400">{s.vertrouwensniveau}</span>
      </div>
      <p className="text-sm text-slate-700">{s.toelichting}</p>
      <div className="mt-3 bg-indigo-50/60 border border-indigo-100 rounded-lg p-3">
        <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 mb-1">Bron · WOR {s.wor_citaat?.artikel}</p>
        <p className="text-xs text-slate-600 leading-relaxed line-clamp-4">{s.wor_citaat?.tekst}</p>
        <p className="text-[11px] text-slate-400 mt-1">{s.wor_citaat?.bron} · {s.wor_citaat?.versie}</p>
      </div>
      <details className="mt-2">
        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-800">Toon documentfragment dat dit signaal veroorzaakte</summary>
        <p className="mt-1.5 text-xs text-slate-600 bg-slate-50 rounded p-2 leading-relaxed">“{s.fragment}”</p>
      </details>
    </div>
  );
};

const OrAnalyse = () => {
  const [tab, setTab] = useState("tekst");
  const [tekst, setTekst] = useState("");
  const [file, setFile] = useState(null);
  const [naam, setNaam] = useState("");
  const [bewaar, setBewaar] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rapport, setRapport] = useState(null);
  const [docs, setDocs] = useState([]);
  const [exporting, setExporting] = useState(false);

  const loadDocs = () => api.get("/or/documents").then((r) => setDocs(r.data)).catch(() => setDocs([]));
  useEffect(() => { loadDocs(); }, []);

  const exporteerPdf = async () => {
    if (!rapport?.analyse_id) return;
    setExporting(true);
    try {
      const res = await api.get(`/or/analyses/${rapport.analyse_id}/export`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OR-analyse_${(rapport.documentnaam || "rapport").replace(/[^\w-]+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF geëxporteerd.");
    } catch (e) {
      toast.error(e.response?.status === 404 ? "Analyse niet gevonden." : "Exporteren mislukt.");
    } finally { setExporting(false); }
  };

  const analyseer = async () => {
    setBusy(true); setRapport(null);
    try {
      let body = {};
      if (tab === "tekst") {
        if (!tekst.trim()) { toast.error("Plak eerst de documenttekst."); setBusy(false); return; }
        if (bewaar && naam.trim()) {
          // sla platte tekst op als .txt via upload-endpoint
          const blob = new Blob([tekst], { type: "text/plain" });
          const fd = new FormData();
          fd.append("file", blob, `${naam.trim()}.txt`);
          fd.append("documentnaam", naam.trim()); fd.append("versie", "1"); fd.append("bewaar", "true");
          const up = await api.post("/or/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          body = { doc_id: up.data.doc_id, documentnaam: naam.trim() };
          loadDocs();
        } else {
          body = { tekst, documentnaam: naam.trim() || "Geplakte tekst" };
        }
      } else {
        if (!file) { toast.error("Kies een bestand."); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file); fd.append("documentnaam", naam.trim() || file.name);
        fd.append("versie", "1"); fd.append("bewaar", bewaar ? "true" : "false");
        const up = await api.post("/or/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        body = up.data.doc_id ? { doc_id: up.data.doc_id, documentnaam: naam.trim() || file.name }
                              : { tekst: up.data.tekst, documentnaam: naam.trim() || file.name };
        if (up.data.bewaard) loadDocs();
      }
      const res = await api.post("/or/analyse", body);
      setRapport(res.data);
      toast.success("Analyse gereed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse mislukt.");
    } finally { setBusy(false); }
  };

  const analyseerBewaard = async (doc) => {
    setBusy(true); setRapport(null);
    try {
      const res = await api.post("/or/analyse", { doc_id: doc.id, documentnaam: doc.documentnaam });
      setRapport(res.data); toast.success("Analyse gereed.");
    } catch (e) { toast.error(e.response?.data?.detail || "Analyse mislukt."); }
    finally { setBusy(false); }
  };

  const verwijder = async (doc) => {
    if (!window.confirm(`'${doc.documentnaam}' verwijderen?`)) return;
    try { await api.delete(`/or/documents/${doc.id}`); toast.success("Verwijderd."); loadDocs(); }
    catch { toast.error("Verwijderen mislukt."); }
  };

  return (
    <div className="animate-fade-up" data-testid="or-analyse-page">
      <PageHeader title="OR-documentanalyse" icon={ClipboardCheck} accent={ACCENT}
        subtitle="Lever een OR-document aan (bijv. een voorgenomen besluit of regeling). De app signaleert indicatief mogelijke advies- (WOR art. 25) en instemmingsplicht (art. 27) en bredere OR-relevantie, altijd met bronverwijzing naar de WOR." />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex gap-2 mb-4">
              <button data-testid="or-tab-tekst" onClick={() => setTab("tekst")} className={`text-sm font-medium px-3 py-1.5 rounded-lg ${tab === "tekst" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>Tekst plakken</button>
              <button data-testid="or-tab-bestand" onClick={() => setTab("bestand")} className={`text-sm font-medium px-3 py-1.5 rounded-lg ${tab === "bestand" ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-600"}`}>Bestand uploaden</button>
            </div>
            {tab === "tekst" ? (
              <Textarea data-testid="or-tekst-input" value={tekst} onChange={(e) => setTekst(e.target.value)} rows={9}
                placeholder="Plak hier de tekst van het OR-document…" className="resize-none" />
            ) : (
              <div>
                <Label className="text-sm">Bestand (PDF, DOCX of TXT)</Label>
                <Input data-testid="or-file-input" type="file" accept=".pdf,.docx,.txt" onChange={(e) => setFile(e.target.files[0])} />
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
              <div><Label className="text-sm">Documentnaam</Label>
                <Input data-testid="or-naam-input" value={naam} onChange={(e) => setNaam(e.target.value)} placeholder="Bijv. Reorganisatieplan 2026" /></div>
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input data-testid="or-bewaar" type="checkbox" checked={bewaar} onChange={(e) => setBewaar(e.target.checked)} className="rounded border-slate-300" />
                Opslaan voor later gebruik (privé)
              </label>
            </div>
            <Button data-testid="or-analyseer-btn" onClick={analyseer} disabled={busy} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-2" />}
              {busy ? "Analyseren…" : "Analyseer document"}
            </Button>
          </div>

          {rapport && (
            <div data-testid="or-rapport" className="space-y-4">
              <div className="bg-white border border-slate-200 rounded-xl p-5">
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <h2 className="font-heading text-lg font-bold text-slate-900">Resultaat{rapport.documentnaam ? `: ${rapport.documentnaam}` : ""}</h2>
                  <Button data-testid="or-export-pdf-btn" onClick={exporteerPdf} disabled={exporting} size="sm" variant="outline" className="h-8 border-slate-300 text-slate-700">
                    {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                    Exporteer als PDF
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-lg px-3 py-2 bg-blue-50" data-testid="or-sum-advies">
                    <p className="text-[11px] text-blue-700">Mogelijk adviesplichtig</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.advies}</p>
                  </div>
                  <div className="rounded-lg px-3 py-2 bg-emerald-50" data-testid="or-sum-instemming">
                    <p className="text-[11px] text-emerald-700">Mogelijk instemmingsplichtig</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.instemming}</p>
                  </div>
                  <div className="rounded-lg px-3 py-2 bg-slate-50" data-testid="or-sum-overig">
                    <p className="text-[11px] text-slate-500">Overige OR-relevantie</p>
                    <p className="text-xl font-heading font-bold text-slate-900">{rapport.samenvatting.overig}</p>
                  </div>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2.5">
                  <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" /> {rapport.disclaimer}
                </div>
              </div>
              {rapport.signalen.length === 0 ? (
                <div data-testid="or-geen-signalen" className="bg-white border border-slate-200 rounded-xl p-5 text-sm text-slate-500 flex items-center gap-2">
                  <Info className="w-4 h-4 text-slate-400" /> Geen duidelijke koppeling met de WOR gevonden op basis van de bronnen. Dit betekent niet dat er geen OR-recht van toepassing is — raadpleeg bij twijfel de OR.
                </div>
              ) : (
                <div className="space-y-3">
                  {rapport.signalen.map((s, i) => <SignaalKaart key={i} s={s} />)}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-1"><ScrollText className="w-4 h-4 text-indigo-600" />
              <h3 className="font-heading font-bold text-slate-900">Mijn documenten</h3></div>
            <p className="text-xs text-slate-500 mb-3">Privé opgeslagen. Niet zichtbaar voor anderen en niet toegevoegd aan de kennisbank.</p>
            {docs.length === 0 ? (
              <p data-testid="or-geen-docs" className="text-xs text-slate-400">Nog geen opgeslagen documenten.</p>
            ) : (
              <ul className="space-y-2" data-testid="or-docs-lijst">
                {docs.map((g) => g.versies.map((d) => (
                  <li key={d.id} data-testid={`or-doc-${d.id}`} className="border border-slate-200 rounded-lg p-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />{d.documentnaam}</p>
                        <p className="text-[11px] text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDateTime(d.created_at)} · v{d.versie}</p>
                      </div>
                      <button data-testid={`or-doc-del-${d.id}`} onClick={() => verwijder(d)} className="text-slate-300 hover:text-red-600 shrink-0"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Button data-testid={`or-doc-analyse-${d.id}`} onClick={() => analyseerBewaard(d)} disabled={busy} size="sm" variant="outline" className="mt-2 h-7 w-full border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                      Opnieuw analyseren
                    </Button>
                  </li>
                )))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrAnalyse;
